/**
 * UK environmental regulator inference from a postcode (or region name).
 *
 *   EA   — Environment Agency (England)
 *   NRW  — Natural Resources Wales
 *   SEPA — Scottish Environment Protection Agency
 *   NIEA — Northern Ireland Environment Agency
 *
 * Scotland postcode prefixes (areas before the digit):
 *   AB, DD, DG, EH, FK, G, HS, IV, KA, KW, KY, ML, PA, PH, TD, ZE
 * Wales:
 *   CF, LD, LL, NP, SA, SY (and CH for parts of Chester/Wrexham border — falls back to EA by default)
 * Northern Ireland:
 *   BT
 * Everything else (alphabetic prefix) → EA (England).
 *
 * Returns the standard code used elsewhere in the app:
 *   'UK_EA' | 'UK_NRW' | 'UK_SEPA' | 'UK_NIEA'
 */

const SCOTLAND_PREFIXES = new Set([
  'AB','DD','DG','EH','FK','G','HS','IV','KA','KW','KY','ML','PA','PH','TD','ZE'
]);
const WALES_PREFIXES = new Set([
  'CF','LD','LL','NP','SA','SY'
]);
const NI_PREFIXES = new Set(['BT']);

export type UKRegulatorCode = 'UK_EA' | 'UK_NRW' | 'UK_SEPA' | 'UK_NIEA';

export function inferUKRegulator(postcodeOrRegion?: string | null): UKRegulatorCode {
  if (!postcodeOrRegion) return 'UK_EA';
  const raw = postcodeOrRegion.trim().toUpperCase();
  // Region name shortcuts
  if (/SCOTLAND|SCOTTISH/i.test(raw)) return 'UK_SEPA';
  if (/WALES|CYMRU/i.test(raw))       return 'UK_NRW';
  if (/NORTHERN.?IRELAND|^NI$/i.test(raw)) return 'UK_NIEA';

  // Postcode area = leading letters before any digit (e.g. DG2 8LT → "DG", G42 → "G")
  const m = raw.match(/^([A-Z]{1,2})/);
  if (!m) return 'UK_EA';
  const area = m[1];
  if (NI_PREFIXES.has(area))       return 'UK_NIEA';
  if (SCOTLAND_PREFIXES.has(area)) return 'UK_SEPA';
  if (WALES_PREFIXES.has(area))    return 'UK_NRW';
  return 'UK_EA';
}

export const UK_REGULATOR_LABEL: Record<UKRegulatorCode, string> = {
  UK_EA:   'Environment Agency (England)',
  UK_NRW:  'Natural Resources Wales',
  UK_SEPA: 'Scottish Environment Protection Agency',
  UK_NIEA: 'Northern Ireland Environment Agency',
};
